/**
 * 보고서 상세의 back-link 해석 — 진입점(entry state) 우선, 없으면 type 기본값.
 *
 * 상세는 `/reports/:id` 하나이고 렌더도 `ComposerReportDetail` 하나다(유형별 registry·dispatcher 없음).
 * 진입 소스가 `navigate(url, { state: { from, fromLabel } })`로 옵트인한 진입점을
 * 상세·형제 칩·결재함 진입·배포 설정 화면이 **똑같이** 존중해야 하므로 해석 규칙을 여기 한 곳에 둔다.
 * (갈래가 여럿이던 시절 분기마다 따로 쓰다 TBM 분기만 누락돼, 결재함에서 연 TBM의 뒤로가기가
 *  문서관리로 새는 버그가 있었다.)
 */

/** 진입 소스가 `location.state`로 넘기는 back-link 정보. */
export interface DetailEntryState {
  /** 돌아갈 경로(쿼리 포함 가능 — 예: `/reports?page=3`). */
  from?: string;
  /** 뒤로가기 버튼 라벨(예: `결재함`). */
  fromLabel?: string;
  /**
   * 흐름이 끝난 뒤 되돌아갈 **원점**(F-52). `from/fromLabel`과 축이 다르다:
   * 저쪽은 「이 화면의 「←」가 갈 곳」(= 바로 전 화면)이고, 이쪽은 「이 흐름을 시작하기 전에
   * 있던 곳」이다. 상세 → 배포 설정처럼 **한 화면 더 깊이** 들어가는 이동에서만 갈린다 —
   * 「←」는 상세로 돌아가야 하고(진입점), 설정을 확정하고 나면 목록·점검 상세로 나가야 한다(원점).
   *
   * **키의 있고 없음이 곧 답이다**(폴백 규칙은 {@link resolveDetailEntryOrigin}이 소유한다):
   * - 없음 = 「원점을 따로 생각하지 않았다」 → `from/fromLabel`이 곧 원점(작성 화면 → 설정처럼
   *   상세를 거치지 않고 들어온 경우)
   * - 있고 비어 있음 = 「생각해 봤는데 물려줄 원점이 없다」 → 진입점으로 폴백하지 **않고**
   *   목적지 화면의 기본값으로 떨어진다. 진입점이 그 자체로 막다른 길일 때 쓴다(F-52 — 상세가
   *   설정 화면에서 들어왔으면 확정 뒤 그리로 되돌려 보낼 수 없다).
   */
  origin?: { from?: string; fromLabel?: string };
}

/** 상세 헤더가 쓰는 back-link 쌍. */
export interface DetailBackNav {
  backLink: string;
  backLabel: string;
}

/**
 * `location.state`를 back-link 정보로 해석한다.
 * state는 임의 값이 올 수 있으므로(직접 URL 진입·새로고침 시 null) 방어적으로 좁힌다.
 */
export function readDetailEntryState(state: unknown): DetailEntryState | null {
  if (!state || typeof state !== 'object') return null;
  const { from, fromLabel, origin } = state as Record<string, unknown>;
  return {
    from: typeof from === 'string' && from ? from : undefined,
    fromLabel: typeof fromLabel === 'string' && fromLabel ? fromLabel : undefined,
    origin: readEntryOrigin(origin),
  };
}

/**
 * `origin`을 방어적으로 좁힌다.
 *
 * **빈 객체를 지우지 않는다** — 위 필드 doc대로 「키가 있고 비어 있음」은 *원점 없음을 명시한
 * 것*이라 진입점 폴백을 막아야 한다. 객체가 아닌 값(문자열·숫자·null)만 「키 없음」으로 본다.
 */
function readEntryOrigin(value: unknown): DetailEntryState['origin'] {
  if (!value || typeof value !== 'object') return undefined;
  const { from, fromLabel } = value as Record<string, unknown>;
  return {
    from: typeof from === 'string' && from ? from : undefined,
    fromLabel: typeof fromLabel === 'string' && fromLabel ? fromLabel : undefined,
  };
}

/**
 * 흐름이 끝난 뒤 되돌아갈 **원점** 쌍을 뽑는다(F-52).
 *
 * 원점을 명시했으면 그것(비어 있어도 그것이 답이다), 아니면 진입점이 곧 원점이다. 이 폴백이
 * 있어야 상세를 거치지 않고 들어온 경로(작성 완료 → 설정 직행)가 종전대로 동작한다.
 */
export function resolveDetailEntryOrigin(state: unknown): {
  from?: string;
  fromLabel?: string;
} {
  const entry = readDetailEntryState(state);
  return entry?.origin ?? { from: entry?.from, fromLabel: entry?.fromLabel };
}

/**
 * 진입점 state와 type 기본값을 합쳐 최종 back-link을 만든다.
 * from/fromLabel은 **각각** 폴백한다 — 진입점이 경로만 주고 라벨을 생략할 수 있다.
 */
export function resolveDetailBackNav(
  state: unknown,
  fallback: DetailBackNav,
): DetailBackNav {
  const entry = readDetailEntryState(state);
  return {
    backLink: entry?.from ?? fallback.backLink,
    backLabel: entry?.fromLabel ?? fallback.backLabel,
  };
}
